import { useCallback, useLayoutEffect } from 'react';
import type { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { tss } from 'tss-react/mui';
import Modal from '../Modal';
import { Paragraph } from '../Paragraph';
import { Button } from '../Button';

const useStyles = tss.create(({ theme }) => ({
    modal: {
        textAlign: 'center',
    },
    downloadInformation: {
        margin: `${theme.spacing(4)} 0`,
    },
    closeButton: {
        display: 'block',
        margin: `${theme.spacing(4)} auto`,
    },
}));

interface DownloadFileHelperProps {
    /**
     * This is the document that will be downloaded
     */
    url: string;

    /**
     * An optional title for the dialog
     */
    dialogTitle?: ReactNode;

    /**
     * Optional dialog content
     */
    dialogContent?: ReactNode;

    /**
     * Don't display the dialog
     */
    noDialog: boolean;

    /**
     * When true, the file specified in URL will start to download, and the
     * dialog will be displayed unless "noDialog" is true
     */
    open: boolean;

    /**
     * Called when the dialog is closed, or immediately if "noDialog" is true
     */
    onClose(): void;
}

export const DownloadFileHelper: FC<DownloadFileHelperProps> = ({
    dialogTitle,
    dialogContent,
    noDialog,
    onClose,
    open,
    url,
}) => {
    const { classes } = useStyles();
    const download = useCallback(() => {
        window.location.href = url;
    }, [url]);

    useLayoutEffect(() => {
        if (open) {
            download();
        }
        if (noDialog) {
            onClose();
        }
    }, [open, noDialog, onClose, download]);
    return (
        <>
            <Modal
                open={open && !noDialog}
                closeModal={onClose}
                className={classes.modal}
                header={{
                    title: dialogTitle,
                }}
            >
                {dialogContent}
                <Paragraph className={classes.downloadInformation}>
                    <FormattedMessage
                        defaultMessage="The download should start immediately. If not, <Link>click here to download</Link>."
                        description="Download file dialog"
                        values={{
                            Link: (chunks) => (
                                <a
                                    href={url}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        download();
                                    }}
                                >
                                    {chunks}
                                </a>
                            ),
                        }}
                    />
                </Paragraph>
                <Button
                    onClick={() => onClose()}
                    className={classes.closeButton}
                >
                    <FormattedMessage
                        defaultMessage="Close"
                        description="Download file dialog: Close button"
                    />
                </Button>
            </Modal>
        </>
    );
};

export default DownloadFileHelper;
